import React, { useState, useRef, useEffect } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

const CmsElementImageCompare = ({ index = '0', ...props }) => {
  // Local state for the slider position (0-100%)
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses,
  } = useAbstractCmsElement({ index, ...props });

  // Update container width to keep the overlay image size stable
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const imageUrl = getParamValue('imageUrl', '');
  const compareImageUrl = getParamValue('compareImageUrl', '');

  const shownClass = shown ? 'elem-shown' : 'elem-hidden';

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-image-compare relative ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      style={getBackgroundStyle()}
    >
      {elementStyleTag}

      {imageUrl && compareImageUrl ? (
        <div
          className="compare-wrapper"
          ref={containerRef}
          style={{ position: 'relative', width: '100%', overflow: 'hidden', userSelect: 'none' }}
        >
          {/* Base Image (Bottom / "After") */}
          <img
            src={compareImageUrl}
            style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
            alt="After"
          />

          {/* Overlay Image (Top / "Before") */}
          <div
            className="compare-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${sliderPos}%`,
              height: '100%',
              overflow: 'hidden',
              borderRight: '2px solid white',
            }}
          >
            <img
              src={imageUrl}
              style={{
                width: containerWidth ? `${containerWidth}px` : '100vw',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                maxWidth: 'none',
              }}
              alt="Before"
            />
          </div>

          {/* Range Slider for Interaction */}
          <input
            type="range"
            min="0"
            max="100"
            value={sliderPos}
            onChange={(e) => setSliderPos(e.target.value)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'col-resize',
              zIndex: 10,
            }}
          />

          {/* Visual Handle / Divider Line */}
          <div
            className="compare-handle"
            style={{
              position: 'absolute',
              top: 0,
              left: `${sliderPos}%`,
              bottom: 0,
              width: '4px',
              backgroundColor: 'white',
              marginLeft: '-2px',
              pointerEvents: 'none',
              zIndex: 5,
              boxShadow: '0 0 10px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '40px',
                height: '40px',
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              }}
            >
              <i className="fa fa-arrows-alt-h" style={{ color: '#333' }} />
            </div>
          </div>
        </div>
      ) : null}

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    /* Stable wrapper for ScrollTrigger in React 19 */
    <div>
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
        {parallaxData ? <Parallax {...parallaxData}>{innerContent}</Parallax> : innerContent}
      </CustomScrollTrigger>
    </div>
  );
};

export default CmsElementImageCompare;
